/**
 * CASE.SHE — Часті запитання.
 *
 * Розкриття відповіді керується звідси, а не декларативним CSS. Попередня
 * реалізація на `::details-content` + `interpolate-size` + `content-visibility`
 * з `allow-discrete` не пройшла ручний QA: `interpolate-size` є лише в
 * Chromium, тож у Safari висота стрибала, а дискретне перемикання
 * `content-visibility` давало на телефоні короткий кадр уже видимого вмісту.
 *
 * Тут висотою володіє один custom element на весь список:
 *   - клік по `summary` перехоплюється, тому браузер сам `open` не перемикає
 *     і кадру з повнорозмірним вмістом не існує;
 *   - при відкритті спочатку виставляється контрольований нульовий стан,
 *     і лише потім з'являється `open`, після чого вимірюється реальна висота;
 *   - при закритті `open` тримається до кінця анімації;
 *   - висота, прозорість і рух тексту стартують одним кадром;
 *   - службові inline-стилі знімаються після завершення.
 *
 * Слухач один і делегований, прибирається в `disconnectedCallback`, тож
 * повторний рендер секції в Theme Editor нічого не дублює. Без цього файлу
 * секція лишається звичайним робочим `details`.
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** Значення за замовчуванням, якщо CSS-токени секції недоступні. */
const FALLBACK = { open: 300, close: 240, rise: 4 };
const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

/**
 * Перервана анімація не має програвати повну тривалість заради кількох
 * пікселів, але й миттєвого перемикання бути не повинно.
 */
const MIN_DURATION = 120;

class CaseSheFaq extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /**
   * Активні анімації по кожному `details`. `opening` зберігає намір останнього
   * натискання: під час закриття `open` ще `true`, тож самого атрибута мало.
   * @type {Map<HTMLDetailsElement, { height: Animation, body: Animation, opening: boolean }>}
   */
  #running = new Map();

  connectedCallback() {
    this.#controller = new AbortController();
    this.addEventListener('click', this.#handleClick, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    for (const details of [...this.#running.keys()]) this.#settle(details);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Посилання всередині відповіді лежать поза `summary` і сюди не потрапляють.
    const summary = target.closest('summary');
    if (!summary || !this.contains(summary)) return;

    const details = summary.closest('details[data-cs-fq-item]');
    if (!(details instanceof HTMLDetailsElement) || !this.contains(details)) return;

    const panel = details.querySelector('[data-cs-fq-panel]');
    const body = details.querySelector('[data-cs-fq-body]');
    if (!(panel instanceof HTMLElement) || !(body instanceof HTMLElement)) return;

    // З цього моменту станом керуємо ми, а не браузер.
    event.preventDefault();

    const running = this.#running.get(details);
    const goingOpen = running ? running.opening : details.open;

    if (goingOpen) {
  this.#collapse(details, panel, body);
} else {
  this.#collapseOthers(details);
  this.#expand(details, panel, body);
}
};

/**
 * Закриває інші відкриті пункти перед відкриттям нового.
 *
 * @param {HTMLDetailsElement} current
 */
#collapseOthers(current) {
  const items = this.querySelectorAll('details[data-cs-fq-item]');

  for (const details of items) {
    if (!(details instanceof HTMLDetailsElement) || details === current) {
      continue;
    }

    const running = this.#running.get(details);
    const goingOpen = running ? running.opening : details.open;

    if (!goingOpen) continue;

    const panel = details.querySelector('[data-cs-fq-panel]');
    const body = details.querySelector('[data-cs-fq-body]');

    if (!(panel instanceof HTMLElement) || !(body instanceof HTMLElement)) {
      continue;
    }

    this.#collapse(details, panel, body);
  }
}

  /**
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement} panel
   * @param {HTMLElement} body
   */
  #expand(details, panel, body) {
    const from = this.#currentState(details, panel, body);

    // Нульовий стан виставляється до `open`, в одному завданні й без
    // проміжного кадру, тому вмісту на повну висоту браузер не покаже.
    panel.style.overflow = 'hidden';
    panel.style.height = `${from.height}px`;
    details.open = true;

    // Вміст уже в потоці, але обрізаний нульовою висотою — можна міряти.
    const full = body.getBoundingClientRect().height;
    const duration = this.#duration(this.#token('--cs-fq-open', FALLBACK.open), full - from.height, full);

    this.#run(details, panel, body, from, { height: full, opacity: 1, transform: 'translateY(0px)' }, duration, true);
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement} panel
   * @param {HTMLElement} body
   */
  #collapse(details, panel, body) {
    const from = this.#currentState(details, panel, body);
    const full = body.getBoundingClientRect().height || from.height;
    const rise = this.#token('--cs-fq-rise', FALLBACK.rise);

    panel.style.overflow = 'hidden';
    panel.style.height = `${from.height}px`;

    const duration = this.#duration(this.#token('--cs-fq-close', FALLBACK.close), from.height, full);

    this.#run(
      details,
      panel,
      body,
      from,
      { height: 0, opacity: 0, transform: `translateY(-${rise}px)` },
      duration,
      false
    );
  }

  /**
   * Знімає фактичні значення, з яких починати, і скасовує те, що вже грає.
   * Читання йде до `cancel()`, інакше повернулися б стилі спокою.
   *
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement} panel
   * @param {HTMLElement} body
   */
  #currentState(details, panel, body) {
    const running = this.#running.get(details);
    const live = details.open || Boolean(running);
    const rise = this.#token('--cs-fq-rise', FALLBACK.rise);

    let state;
    if (live) {
      const computed = getComputedStyle(body);
      const transform = computed.transform;
      state = {
        height: panel.getBoundingClientRect().height,
        opacity: Number.parseFloat(computed.opacity) || 0,
        transform: transform && transform !== 'none' ? transform : 'translateY(0px)',
      };
    } else {
      state = { height: 0, opacity: 0, transform: `translateY(-${rise}px)` };
    }

    if (running) {
      running.height.cancel();
      running.body.cancel();
      this.#running.delete(details);
    }

    return state;
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement} panel
   * @param {HTMLElement} body
   * @param {{ height: number, opacity: number, transform: string }} from
   * @param {{ height: number, opacity: number, transform: string }} to
   * @param {number} duration
   * @param {boolean} opening
   */
  #run(details, panel, body, from, to, duration, opening) {
    const options = { duration, easing: EASING, fill: 'forwards' };

    // Обидві анімації створюються в одному кадрі, тож висота, прозорість і
    // рух тексту стартують разом.
    const height = panel.animate([{ height: `${from.height}px` }, { height: `${to.height}px` }], options);
    const bodyAnimation = body.animate(
      [
        { opacity: from.opacity, transform: from.transform },
        { opacity: to.opacity, transform: to.transform },
      ],
      options
    );

    const record = { height, body: bodyAnimation, opening };
    this.#running.set(details, record);

    height.finished
      .then(() => {
        // Нове натискання вже все перебрало на себе.
        if (this.#running.get(details) !== record) return;

        // Порядок важливий: `fill: forwards` ще тримає кінцевий стан, тому
        // ні зняття `open`, ні очищення inline-стилів не дають зайвого кадру.
        if (!opening) details.open = false;
        this.#running.delete(details);
        panel.style.height = '';
        panel.style.overflow = '';
        height.cancel();
        bodyAnimation.cancel();
      })
      .catch(() => {
        // `cancel()` відхиляє promise — прибирає вже той, хто скасував.
      });
  }

  /**
   * Знімає анімацію і всі службові стилі, лишаючи елемент у стані спокою.
   *
   * @param {HTMLDetailsElement} details
   */
  #settle(details) {
    const running = this.#running.get(details);
    if (running) {
      running.height.cancel();
      running.body.cancel();
      this.#running.delete(details);
    }

    const panel = details.querySelector('[data-cs-fq-panel]');
    if (panel instanceof HTMLElement) {
      panel.style.height = '';
      panel.style.overflow = '';
    }
  }

  /**
   * Пропорційно скорочує тривалість, коли шлях коротший за повний.
   *
   * @param {number} base
   * @param {number} distance
   * @param {number} full
   */
  #duration(base, distance, full) {
    /*
     * Без руху лишається той самий життєвий цикл, лише нульової тривалості:
     * контрольований стартовий стан і вимірювання нікуди не діваються, тож
     * перший кадр виходить одразу правильний, а перемикання — миттєве.
     */
    if (window.matchMedia(REDUCED_MOTION_QUERY).matches) return 0;
    if (!full || distance >= full) return base;
    return Math.max(MIN_DURATION, Math.round((base * Math.abs(distance)) / full));
  }

  /**
   * Шкала руху живе в CSS секції — тут лише читається.
   *
   * @param {string} name
   * @param {number} fallback
   */
  #token(name, fallback) {
    const raw = getComputedStyle(this).getPropertyValue(name).trim();
    const value = Number.parseFloat(raw);
    if (!Number.isFinite(value)) return fallback;
    return raw.endsWith('ms') || !raw.endsWith('s') ? value : value * 1000;
  }
}

if (!customElements.get('case-she-faq')) {
  customElements.define('case-she-faq', CaseSheFaq);
}
